// #region imports

// --- Angular
import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';

// --- RxJs
import { Subject, Subscription, timer } from 'rxjs';
import { map, switchMap, shareReplay } from 'rxjs/operators';

// --- 3rd Party
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { SurveySummaryModel, UserDetailsModel, SurveySummaryBasicModel, Constants } from '../../../models';
import { GoogleAnalyticsService } from '../../../core/services/google-analytics.service';

// #endregion

@Component({
  selector: 'hs-dash-header-strip',
  templateUrl: './dash-header-strip.component.html',
  styleUrls: ['./dash-header-strip.component.scss'],
})
export class DashHeaderStripComponent implements OnInit, OnChanges, OnDestroy {

  @Input() userDetails: UserDetailsModel | null = null;
  @Input() surveySummaries: SurveySummaryModel[] | null = null;
  @Input() selectedSurvey: SurveySummaryModel | null = null;
  @Input() isMobile: boolean | null = null;

  @Output() surveyClick = new EventEmitter<string>();
  @Output() openSurveySummaries = new EventEmitter<SurveySummaryBasicModel>();

  private surveySummariesSubject = new Subject<SurveySummaryModel[]>();
  surveySummariesInDropDown$ = this.surveySummariesSubject.pipe(shareReplay(1));

  timeOfDay: string;
  timeOfDaySubscription: Subscription = Subscription.EMPTY;
  languageChangedSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private translate: TranslateService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.timeOfDay = '';
  }

  ngOnInit() {
    this.timeOfDaySubscription = timer(0, 60000)
      .pipe(
        map(() => this._getTimeKey()),
        switchMap((timeKey) => this.translate.get(timeKey))
      )
      .subscribe((text) => (this.timeOfDay = text));

    this.languageChangedSubscription = this.translate.onLangChange
      .pipe(
        map(() => this._getTimeKey()),
        switchMap((timeKey) => this.translate.get(timeKey))
      )
      .subscribe((text) => (this.timeOfDay = text));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['surveySummaries'] && changes['surveySummaries'].currentValue) {
      const surveySummaries = changes['surveySummaries'].currentValue.slice(0, Constants.DEFAULT_PAGE_SIZE);
      this.surveySummariesSubject.next(surveySummaries);
    }
  }

  ngOnDestroy() {
    this.timeOfDaySubscription.unsubscribe();
    this.timeOfDaySubscription = Subscription.EMPTY;

    if (this.languageChangedSubscription) {
      this.languageChangedSubscription.unsubscribe();
      this.languageChangedSubscription = Subscription.EMPTY;
    }
  }

  onSurveyClick(surveyId: string): void {
    this.googleAnalyticsService.eventEmitter(
      'selected_survey',
      'dashboard',
      'selected_survey_changed',
      `Selected Survey Changed = ${surveyId}`
    );
    this.surveyClick.emit(surveyId);
  }

  onOpenSurveySummariesOpen(
    surveySummaries: SurveySummaryModel[] | null,
    selectedSurvey: SurveySummaryModel | null,
  ) {
    this.openSurveySummaries.emit({
      surveySummaries,
      selectedSurvey,
    });
  }

  private _getTimeKey() {
    let timeKey = '';
    const hour = moment().get('hour');
    if (hour < 12) {
      timeKey = 'todMorning';
    } else if (hour < 18) {
      timeKey = 'todAfternoon';
    } else {
      timeKey = 'todEvening';
    }
    return timeKey;
  }
}
